// Settings.jsx - Clean Professional Design
import { useEffect, useState, useCallback } from "react";
import { 
  fetchSettings, 
  updateSettings, 
  getScreenshotInterval,
  updateScreenshotInterval,
  getScreenshotStatus,
  testAutoScreenshot,
  fetchWorkspaces 
} from "../api/api";
import "./Settings.css";

const Settings = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [screenshotStatus, setScreenshotStatus] = useState(null);
  const [lastLoadTime, setLastLoadTime] = useState(null);

  // Simple settings state
  const [settings, setSettings] = useState({
    screenshotInterval: 600,
    idleThreshold: 300,
    workHoursStart: "09:00",
    workHoursEnd: "17:00",
    trackingDays: ["mon", "tue", "wed", "thu", "fri"],
    privacyBlur: false,
    screenshotQuality: "medium",
    enableTracking: true,
    dailyReports: true,
    weeklyReports: false
  });

  const [errors, setErrors] = useState({});

  // Memoize load function to prevent infinite re-renders
  const loadWorkspaceSettings = useCallback(async (workspaceId) => {
    // Prevent too frequent loading
    const now = Date.now();
    if (lastLoadTime && (now - lastLoadTime < 2000)) {
      console.log("⏸️ Skipping load - too frequent");
      return;
    }
    
    console.log("🔄 Loading workspace settings for ID:", workspaceId);
    setLastLoadTime(now);
    
    try {
      // Get screenshot interval directly
      const intervalData = await getScreenshotInterval(workspaceId);
      console.log("✅ Got screenshot interval:", intervalData?.screenshot_interval);
      
      if (intervalData && intervalData.screenshot_interval !== undefined) {
        setSettings(prev => {
          // Only update if value actually changed
          if (prev.screenshotInterval === intervalData.screenshot_interval) {
            console.log("📊 No change in interval, skipping update");
            return prev;
          }
          console.log("📊 Updating interval to:", intervalData.screenshot_interval);
          return {
            ...prev,
            screenshotInterval: intervalData.screenshot_interval
          };
        });
      }
      
      // Try to get status (optional)
      try {
        const status = await getScreenshotStatus(workspaceId);
        if (status && status.success !== false) {
          setScreenshotStatus(status.data || null);
        }
      } catch (statusError) {
        // Ignore - not critical
      }
      
    } catch (error) {
      console.error("❌ Error loading workspace settings:", error);
    }
  }, [lastLoadTime]);

  // Load workspaces on mount - ONLY ONCE
  useEffect(() => {
    let mounted = true;
    let loadTimeout = null;

    const loadWorkspaces = async () => {
      try {
        if (user?.id) {
          console.log("🚀 Initial workspace load...");
          const workspacesData = await fetchWorkspaces(user.id);
          
          if (mounted) {
            setWorkspaces(workspacesData || []);
            
            if (workspacesData?.length > 0) {
              const firstWorkspace = workspacesData[0];
              setSelectedWorkspace(firstWorkspace);
              
              // Load settings after a short delay
              loadTimeout = setTimeout(() => {
                if (mounted) {
                  loadWorkspaceSettings(firstWorkspace.id);
                }
              }, 100);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load workspaces:", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadWorkspaces();

    // Cleanup function
    return () => {
      mounted = false;
      if (loadTimeout) clearTimeout(loadTimeout);
    };
  }, [user, loadWorkspaceSettings]);

  // Handle workspace selection
  const handleWorkspaceChange = (workspace) => {
    console.log("🔄 Changing workspace to:", workspace.id);
    setSelectedWorkspace(workspace);
    // Load new workspace settings
    loadWorkspaceSettings(workspace.id);
  };

  // Handle setting changes
  const handleSettingChange = (key, value) => {
    console.log(`✏️ Changing ${key} from ${settings[key]} to ${value}`);
    setSettings(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: "" }));
    }
  };

  // Handle interval change
  const handleIntervalChange = (value) => {
    console.log(`⏱️ Interval change input: ${value}`);
    
    const intValue = parseInt(value);
    if (!isNaN(intValue) && intValue >= 0) {
      handleSettingChange("screenshotInterval", intValue);
    }
  };

  // Validate settings
  const validateSettings = () => {
    const newErrors = {};
    
    if (settings.screenshotInterval !== undefined && settings.screenshotInterval !== 0) {
      if (settings.screenshotInterval < 30) {
        newErrors.screenshotInterval = "Minimum interval is 30 seconds (use 0 to disable)";
      }
    }
    
    if (settings.idleThreshold < 60) {
      newErrors.idleThreshold = "Idle threshold must be at least 60 seconds";
    }
    
    const start = parseInt(settings.workHoursStart.replace(":", ""));
    const end = parseInt(settings.workHoursEnd.replace(":", ""));
    if (start >= end) {
      newErrors.workHours = "End time must be after start time";
    }
    
    return newErrors;
  };

  // MODIFIED: Save settings to ALL workspaces
  const saveSettings = async () => {
    if (workspaces.length === 0) {
      alert("No workspaces found");
      return;
    }

    const validationErrors = validateSettings();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    console.log("💾 Attempting to save screenshot interval to ALL workspaces...");
    console.log("Total workspaces:", workspaces.length);
    console.log("New interval for all:", settings.screenshotInterval);

    setSaving(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      // Update ALL workspaces
      for (const workspace of workspaces) {
        try {
          console.log(`📡 Updating workspace ${workspace.id} (${workspace.name})...`);
          
          const result = await updateScreenshotInterval(
            workspace.id, 
            settings.screenshotInterval
          );
          
          if (result && result.success) {
            successCount++;
            console.log(`✅ Workspace ${workspace.id} updated successfully`);
          } else {
            errorCount++;
            errors.push(`Workspace ${workspace.name}: ${result?.message || 'Unknown error'}`);
            console.error(`❌ Failed to update workspace ${workspace.id}:`, result?.message);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Workspace ${workspace.name}: ${error.message}`);
          console.error(`❌ Error updating workspace ${workspace.id}:`, error.message);
        }
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      // Show summary
      let message = `✅ Screenshot interval updated to ${settings.screenshotInterval} seconds!\n\n`;
      message += `Applied to ${successCount} out of ${workspaces.length} workspaces`;
      
      if (errorCount > 0) {
        message += `\n\n⚠️ ${errorCount} workspaces failed:\n`;
        message += errors.slice(0, 3).join('\n');
        if (errors.length > 3) {
          message += `\n...and ${errors.length - 3} more`;
        }
      }
      
      alert(message);
      
      // Reload current workspace settings to verify
      if (selectedWorkspace) {
        setTimeout(() => {
          loadWorkspaceSettings(selectedWorkspace.id);
        }, 1000);
      }
      
    } catch (error) {
      console.error("❌ Save error:", error);
      alert(`Failed to save settings: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Test screenshot functionality on first workspace
  const handleTestScreenshots = async () => {
    if (workspaces.length === 0) return;
    
    const workspaceToTest = selectedWorkspace || workspaces[0];
    
    try {
      await testAutoScreenshot(workspaceToTest.id);
      alert(`Test screenshot request sent to ${workspaceToTest.name}!`);
    } catch (error) {
      console.error("Test failed:", error);
      alert("Test failed. Please check console.");
    }
  };

  // Reset to defaults
  const resetToDefaults = () => {
    if (window.confirm(`Reset screenshot interval to default (600 seconds) for ALL ${workspaces.length} workspaces?`)) {
      setSettings(prev => ({
        ...prev,
        screenshotInterval: 600
      }));
    }
  };

  // Format interval for display
  const formatInterval = (seconds) => {
    if (seconds === 0) return "Disabled";
    if (seconds < 60) return `${seconds} seconds`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    return `${(seconds / 3600).toFixed(1)} hours`;
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="spinner"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <div className="settings-empty">
        <h2>No Workspaces Found</h2>
        <p>Create a workspace to configure settings.</p>
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <header className="settings-header">
        <div>
          <h1>Workspace Settings</h1>
          <p className="subtitle">Configure monitoring and automation settings</p>
          {/* <div className="global-notice">
            ⚠️ <strong>Note:</strong> Changes apply to ALL {workspaces.length} workspaces
          </div> */}
        </div>
        
        {saveSuccess && (
          <div className="save-success">
            <span className="success-icon">✓</span>
            Settings saved to {workspaces.length} workspaces!
          </div>
        )}
      </header>

      {/* Workspace Selector */}
      {/* <div className="workspace-selector">
        <div className="selector-header">
          <h3>Select Workspace to View</h3>
          <span className="info-text">(Settings apply to all workspaces)</span>
        </div>
        
        <div className="workspace-list">
          {workspaces.map(workspace => (
            <button
              key={workspace.id}
              className={`workspace-btn ${selectedWorkspace?.id === workspace.id ? 'active' : ''}`}
              onClick={() => handleWorkspaceChange(workspace)}
            >
              <span className="workspace-name">{workspace.name}</span>
              <span className="workspace-id">ID: {workspace.id}</span>
            </button>
          ))}
        </div>
      </div> */}

      {/* Main Settings Grid */}
      <div className="settings-grid">
        {/* Auto Screenshots Card */}
        <div className="settings-card">
          <div className="card-header">
            <h2>Auto Screenshots</h2>
            <p className="card-description">Configure automatic screenshot capture</p>
            <div className="scope-indicator">
              <span className="scope-badge">Applies to all workspaces</span>
            </div>
          </div>
          
          <div className="card-content">
            {/* Interval Setting */}
            <div className="setting-group">
              <div className="setting-label">
                <span>Screenshot Interval</span>
                <span className="current-value">{formatInterval(settings.screenshotInterval)}</span>
              </div>
              
              <div className="interval-controls">
                {/* Quick Presets */}
                <div className="interval-presets">
                  <button 
                    className={`preset-btn ${settings.screenshotInterval === 0 ? 'active' : ''}`}
                    onClick={() => handleIntervalChange(0)}
                  >
                    Disabled
                  </button>
                  <button 
                    className={`preset-btn ${settings.screenshotInterval === 30 ? 'active' : ''}`}
                    onClick={() => handleIntervalChange(30)}
                  >
                    30s
                  </button>
                  <button 
                    className={`preset-btn ${settings.screenshotInterval === 60 ? 'active' : ''}`}
                    onClick={() => handleIntervalChange(60)}
                  >
                    1m
                  </button>
                  <button 
                    className={`preset-btn ${settings.screenshotInterval === 300 ? 'active' : ''}`}
                    onClick={() => handleIntervalChange(300)}
                  >
                    5m
                  </button>
                  <button 
                    className={`preset-btn ${settings.screenshotInterval === 600 ? 'active' : ''}`}
                    onClick={() => handleIntervalChange(600)}
                  >
                    10m
                  </button>
                </div>
                
                {/* Custom Input */}
                <div className="custom-interval">
                  <div className="input-with-label">
                    <label>Custom Interval (seconds):</label>
                    <input
                      type="number"
                      min="0"
                      max="86400"
                      value={settings.screenshotInterval}
                      onChange={(e) => handleIntervalChange(e.target.value)}
                      className={`interval-input ${errors.screenshotInterval ? 'error' : ''}`}
                    />
                  </div>
                  {errors.screenshotInterval && (
                    <div className="error-message">{errors.screenshotInterval}</div>
                  )}
                  <div className="input-help">
                    This setting will apply to all {workspaces.length} workspaces when saved
                  </div>
                </div>
              </div>
            </div>

            {/* Test Button */}
            {/* <div className="setting-group">
              <button 
                className="test-btn"
                onClick={handleTestScreenshots}
                disabled={settings.screenshotInterval === 0}
              >
                Test Auto Screenshots
              </button>
              <div className="test-info">
                Test will run on: {selectedWorkspace?.name || workspaces[0]?.name}
              </div>
            </div> */}
          </div>
        </div>

        {/* Tracking Settings Card */}
        <div className="settings-card">
          <div className="card-header">
            <h2>Tracking Settings</h2>
            <p className="card-description">Configure activity monitoring</p>
          </div>
          
          <div className="card-content">
            {/* Idle Detection */}
            <div className="setting-group">
              <div className="setting-label">
                <label htmlFor="idleThreshold">Idle Time Threshold</label>
                {errors.idleThreshold && (
                  <span className="error-text">{errors.idleThreshold}</span>
                )}
              </div>
              <div className="input-with-suffix">
                <input
                  id="idleThreshold"
                  type="number"
                  min="60"
                  max="3600"
                  value={settings.idleThreshold}
                  onChange={(e) => handleSettingChange("idleThreshold", parseInt(e.target.value) || 300)}
                  className={`form-input ${errors.idleThreshold ? 'error' : ''}`}
                />
                <span className="input-suffix">seconds</span>
              </div>
              <div className="setting-description">
                Time before employee is marked as idle
              </div>
            </div>

            {/* Enable Tracking */}
            <div className="setting-group">
              <div className="checkbox-setting">
                <input
                  type="checkbox"
                  id="enableTracking"
                  checked={settings.enableTracking}
                  onChange={(e) => handleSettingChange("enableTracking", e.target.checked)}
                  className="checkbox"
                />
                <label htmlFor="enableTracking">
                  <span className="checkbox-label">Enable Activity Tracking</span>
                  <span className="checkbox-description">Monitor mouse, keyboard, and app usage</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Other cards remain the same... */}
      </div>

      {/* Action Buttons */}
      <div className="settings-actions">
        <div className="action-info">
          <p className="scope-warning">
            ⚠️ <strong>Warning:</strong> These settings will apply to ALL {workspaces.length} workspaces
          </p>
        </div>
        
        <div className="action-buttons">
          <button
            type="button"
            onClick={resetToDefaults}
            className="btn-secondary"
            disabled={saving}
          >
            Reset to Defaults
          </button>
          
          <button
            onClick={saveSettings}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? `Saving to ${workspaces.length} workspaces...` : `Save to All Workspaces`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;